import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Pagination,
  Chip,
  keyframes,
} from "@mui/material";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const AlertsTable = ({
  alerts,
  filterType,
  onClearFilter,
  totalCount,
  page,
  onPageChange,
}) => {
  const headers = [
    "SEVERITY",
    "ALERT TYPE",
    "SUBJECT",
    "STAGE",
    "TIME IDLE",
    "ASSIGNED AGENT",
    "ACTIONS",
  ];

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid #E2E8F0",
        overflow: "hidden",
        animation: `${fadeIn} 0.4s ease-out 0.2s both`,
      }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "#F8FAFC" }}>
            {headers.map((header) => (
              <TableCell
                key={header}
                sx={{
                  fontWeight: 700,
                  fontSize: 12,
                  fontStyle: "bold",
                  color: "#94A3B8",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  borderBottom: "1px solid #E2E8F0",
                  py: 1.75,
                  px: 2,
                }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {alerts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} sx={{ textAlign: "center", py: 6 }}>
                <Typography sx={{ color: "#64748B", fontSize: 14 }}>
                  No alerts found for the selected filter
                </Typography>
                <Button
                  onClick={onClearFilter}
                  sx={{
                    mt: 1,
                    textTransform: "none",
                    fontSize: 13,
                    color: "#7C3AED",
                  }}
                >
                  Show all alerts
                </Button>
              </TableCell>
            </TableRow>
          ) : (
            alerts.map((alert, index) => {
              const SeverityIcon = alert.severityIcon;
              return (
                <TableRow
                  key={alert.id}
                  sx={{
                    animation: `${slideInFromRight} 0.4s ease-out ${0.3 + index * 0.08}s both`,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "#F8FAFC",
                    },
                    "& td": {
                      borderBottom: "1px solid #F1F5F9",
                      py: 2,
                      px: 2,
                    },
                  }}
                >
                  <TableCell>
                    <SeverityIcon
                      sx={{ fontSize: 22, color: alert.severityColor }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{ fontWeight: 600, fontSize: 14, color: "#1E293B" }}
                    >
                      {alert.alertType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{ fontSize: 13, color: "#1E293B", fontWeight: 500 }}
                    >
                      {alert.subject}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={alert.stage}
                      size="small"
                      sx={{
                        bgcolor: alert.stageBg,
                        color: alert.stageColor,
                        fontWeight: 500,
                        fontSize: 11,
                        height: 26,
                        borderRadius: 5,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: alert.timeIdleColor,
                      }}
                    >
                      {alert.timeIdle}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: 12,
                          fontWeight: 600,
                          bgcolor: "#E0E7FF",
                          color: "#4F46E5",
                        }}
                      >
                        {alert.assignedAgent
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </Avatar>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "#1E293B",
                          fontWeight: 500,
                        }}
                      >
                        {alert.assignedAgent}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          bgcolor: "primary.main",
                          textTransform: "none",
                          fontSize: 12,
                          fontWeight: 600,
                          borderRadius: 2,
                          px: 2,
                          py: 0.625,
                          minWidth: "auto",
                          boxShadow: "none",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: "primary.main",
                            boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
                          },
                        }}
                      >
                        Investigate
                      </Button>
                      <Button
                        size="small"
                        sx={{
                          color: "#64748B",
                          border: "1px solid #E2E8F0",
                          textTransform: "none",
                          fontSize: 12,
                          fontWeight: 500,
                          borderRadius: 2,
                          px: 1.5,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: "#F1F5F9",
                            color: "#1E293B",
                          },
                        }}
                      >
                        Reassign
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          bgcolor: "#F8FAFC",
          borderTop: "1px solid #E2E8F0",
          animation: `${fadeIn} 0.4s ease-out 0.5s both`,
        }}
      >
        <Typography sx={{ fontSize: 13, color: "#64748B", fontWeight: 400 }}>
          Showing {alerts.length} of {totalCount} active alerts
          {filterType !== "All" && ` (${filterType})`}
        </Typography>

        <Pagination
          count={3}
          page={page}
          onChange={onPageChange}
          size="small"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            "& .MuiPaginationItem-root": {
              height: 44,
              minWidth: 44,
              borderRadius: 3,
              fontSize: 16,
              fontWeight: 700,
              border: "2px solid transparent",
              color: "#64748B",
              boxSizing: "border-box",
              transition: "all 0.2s ease",
            },
            "& .MuiPaginationItem-root.Mui-selected": {
              bgcolor: "#fff",
              borderColor: "#E2E8F0",
              color: "primary.main",
            },
            "& .MuiPaginationItem-previousNext": {
              bgcolor: "#fff",
              borderColor: "#E2E8F0",
              color: "#94A3B8",
              "& svg": {
                fontSize: 24,
              },
            },
            "& .MuiPaginationItem-root:hover": {
              bgcolor: "#F8FAFC",
            },
            "& .MuiPaginationItem-previousNext.Mui-disabled": {
              borderColor: "#E5E7EB",
              color: "#CBD5E1",
            },
          }}
        />
      </Box>
    </TableContainer>
  );
};

export default AlertsTable;
